import {
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@material-ui/core';
import RoomIcon from '@material-ui/icons/Room';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AutoComplete from '../AutoComplete';
import { useLoadingDialog } from '../../hooks/backdrop';
import { useFeedback } from '../../hooks/feedback';
import { createCondominium } from '../../services/personnelDepartment.service';
import CustomDialog from '../CustomDialog';
import {
  CondominiumCoordinatesDTO,
  CondominiumDTO,
} from '../../models/dto/CondominiumDTO';
import { findCondominiumCoordinates } from '../../services/peopleManagement.service';
import { CondominiumReducerData } from '../../store/reducers/condominiumReducer';
import { AppState } from '../../store';

interface CreateCondominiumDialogProps {
  open: boolean;
  handleClose: () => void;
}

const CreateCondominiumDialog: React.FC<CreateCondominiumDialogProps> = ({
  open,
  handleClose,
}) => {
  const { addFeedback } = useFeedback();
  const { handleLoadingDialog } = useLoadingDialog();
  const [condominiumName, setCondominiumName] = useState('');
  const [condominiumLatitude, setCondominiumLatitude] = useState('');
  const [condominiumLongitude, setCondominiumLongitude] = useState('');

  const [condominiumCoordinates, setCondominiumCoordinates] = useState<
    CondominiumCoordinatesDTO
  >();

  const { condominiums, loading }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const [
    condominiumSelected,
    setCondominiumSelected,
  ] = useState<CondominiumDTO | null>();

  const handleChangeCondominiumCoordinates = (
    event: React.ChangeEvent<{}>,
    value: CondominiumDTO | null,
  ) => {
    setCondominiumSelected(value);
    setCondominiumName(value ? value.referencia.toString() : '');
  };

  const getCondominiumCoordinates = useCallback(
    async (reference: number) => {
      try {
        const { data } = await findCondominiumCoordinates(reference);

        if (!data.latitude || !data.longitude) {
          addFeedback({
            type: 'warning',
            description: 'Não há coordenadas cadastradas, favor cadastrar.',
          });
        }
        setCondominiumCoordinates(data);
      } catch (error) {
        addFeedback({
          type: 'error',
          description: 'Erro ao buscar as coordenadas do condomínio.',
        });
      }
    },
    [addFeedback],
  );

  const clearFormFields = () => {
    setCondominiumSelected(undefined);
    setTimeout(() => setCondominiumSelected(null), 0);
    setCondominiumName('');
    setCondominiumLatitude('');
    setCondominiumLongitude('');
  };

  const handleCloseDialog = () => {
    clearFormFields();
    handleClose();
  };

  const handleNewCondominiumSubmit = async () => {
    try {
      handleLoadingDialog(true);
      await createCondominium(
        condominiumName,
        condominiumLatitude,
        condominiumLongitude,
      );
      addFeedback({
        type: 'success',
        description: `Condomínio criado com sucesso!`,
      });
      clearFormFields();
      handleClose();
    } catch (error) {
      addFeedback({
        type: 'error',
        description: 'Ocorreu um erro ao cadastrar o novo condominio.',
      });
    } finally {
      handleLoadingDialog(false);
    }
  };

  useEffect(() => {
    if (condominiumSelected) {
      getCondominiumCoordinates(condominiumSelected.referencia);
    }
  }, [getCondominiumCoordinates, condominiumSelected]);

  useEffect(() => {
    if (condominiumCoordinates) {
      setCondominiumLatitude(condominiumCoordinates.latitude.toString());
      setCondominiumLongitude(condominiumCoordinates.longitude.toString());
    }
  }, [condominiumCoordinates]);

  return (
    <CustomDialog
      open={open}
      handleClose={handleCloseDialog}
      handleConfirm={handleNewCondominiumSubmit}
      maxWidth="xs"
      okButtonLabel="Salvar"
      disabledOkButton={
        condominiumName.trim().length === 0 ||
        condominiumLatitude.trim().length === 0 ||
        condominiumLongitude.trim().length === 0
      }
      title="Cadastro de condomínio"
      contentDividers
      showCloseButton
      keepMounted={false}
    >
      <FormControl margin="normal" fullWidth variant="outlined">
        <AutoComplete
          value={condominiumSelected ?? null}
          loading={loading}
          options={condominiums.sort((a, b) =>
            a.referencia > b.referencia ? 1 : -1,
          )}
          getOptionLabel={(option: CondominiumDTO) =>
            `${option.referencia} - ${option.nome}`
          }
          onChange={handleChangeCondominiumCoordinates}
          placeholder="Selecione um Condomínio"
        />
      </FormControl>

      <FormControl margin="normal" fullWidth variant="outlined">
        <InputLabel htmlFor="condominium-latitude">Latitude</InputLabel>
        <OutlinedInput
          label="Latitude"
          placeholder="Digite a latitude do condomínio"
          id="condominium-latitude"
          type="number"
          value={condominiumLatitude}
          onChange={event => setCondominiumLatitude(event.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <RoomIcon />
            </InputAdornment>
          }
        />
      </FormControl>

      <FormControl margin="normal" fullWidth variant="outlined">
        <InputLabel htmlFor="condominium-longitude">Longitude</InputLabel>
        <OutlinedInput
          label="Longitude"
          placeholder="Digite a longitude do condomínio"
          id="condominium-longitude"
          type="number"
          value={condominiumLongitude}
          startAdornment={
            <InputAdornment position="start">
              <RoomIcon />
            </InputAdornment>
          }
          onChange={event => setCondominiumLongitude(event.target.value)}
        />
      </FormControl>
    </CustomDialog>
  );
};

export default CreateCondominiumDialog;
